import React, { useState } from "react";
import styled, { keyframes } from "styled-components";

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [25, 50, 100];

// ─── Column type detection ───────────────────────────────────────────────────

export function getColumnConfig(colName) {
  const upper = (colName || "").toUpperCase();
  if (upper === "ID" || upper.endsWith("_ID") || upper.endsWith("_NAME")) {
    return { minWidth: 160, type: "id" };
  }
  if (
    upper.includes("TIMESTAMP") ||
    upper.endsWith("_TIME") ||
    upper.endsWith("_AT") ||
    upper.endsWith("_DATE")
  ) {
    return { minWidth: 180, type: "timestamp" };
  }
  if (
    upper.includes("COUNT") ||
    upper.startsWith("NUM_") ||
    upper.endsWith("_NUM") ||
    upper.endsWith("_COUNT")
  ) {
    return { minWidth: 90, type: "number" };
  }
  if (
    upper.includes("NAME") ||
    upper.includes("REASON") ||
    upper.includes("DESCRIPTION") ||
    upper.includes("STATUS")
  ) {
    return { minWidth: 200, type: "text" };
  }
  return { minWidth: 120, type: "text" };
}

// ─── Animations ──────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px) scale(0.9); }
  to   { opacity: 1; transform: translateY(0)   scale(1);   }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Wrapper = styled.div`
  margin: 8px 0;
`;

const TableTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 6px;
`;

const ScrollArea = styled.div`
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px 6px 0 0;
`;

const StyledTable = styled.table`
  width: 100%;
  min-width: max-content;
  border-collapse: collapse;
  font-size: 14px;
  color: #374151;

  tbody tr:nth-child(odd) {
    background: #ffffff;
  }
  tbody tr:nth-child(even) {
    background: #f9fafb;
  }
  tbody tr:hover {
    background: #f3f4f6;
  }
`;

const Th = styled.th`
  background: #f9fafb;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  color: #6b7280;
  padding: 7px 12px;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
`;

const ThInner = styled.div`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
  line-height: 1.35;
`;

const Td = styled.td`
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
  color: #1a1a1a;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;
  cursor: ${({ $copyable }) => ($copyable ? "pointer" : "default")};

  &:hover {
    background: ${({ $copyable }) => ($copyable ? "#f3f4f6" : "inherit")};
  }
`;

const CopiedBadge = styled.span`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: #10b981;
  color: #ffffff;
  border-radius: 3px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 600;
  pointer-events: none;
  white-space: nowrap;
  animation: ${fadeIn} 0.15s ease;
`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-top: none;
  border-radius: 0 0 6px 6px;
  font-size: 14px;
  color: #6b7280;
`;

const PageSizeLabel = styled.span`
  white-space: nowrap;
`;

const PageSizeSelect = styled.select`
  font-size: 14px;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 2px 4px;
  background: white;
  cursor: pointer;
`;

const RangeText = styled.span`
  flex: 1;
  text-align: center;
  white-space: nowrap;
`;

const NavButton = styled.button`
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    color: #d1d5db;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    background: #f3f4f6;
  }
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 2px 10px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;
  margin-left: auto;

  &:hover {
    background: #f3f4f6;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function DataTable({ title, columns, rows, totalRows }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [copiedKey, setCopiedKey] = useState(null);

  if (!rows || rows.length === 0) return null;

  const totalPages = Math.ceil(rows.length / pageSize);
  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, rows.length);
  const displayRows = rows.slice(startIdx, endIdx);

  const columnConfigs = columns.map((col) => getColumnConfig(col));

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleCopy = (key, text) => {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const handleDownload = () => {
    const escape = (val) => {
      const str = val == null ? "" : String(val);
      return str.includes(",") || str.includes('"') || str.includes("\n")
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };
    const csvLines = [
      columns.map(escape).join(","),
      ...rows.map((row) => row.map(escape).join(",")),
    ];
    const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = title ? `${title.replace(/\s+/g, "_")}.csv` : "data.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Wrapper>
      {title && <TableTitle data-testid="datatable-title">{title}</TableTitle>}
      <ScrollArea>
        <StyledTable role="table" data-testid="datatable-table">
          <thead>
            <tr>
              {columns.map((col, colIdx) => (
                <Th
                  key={col}
                  style={{ minWidth: columnConfigs[colIdx].minWidth }}
                >
                  <ThInner>{col}</ThInner>
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {row.map((cell, colIdx) => {
                  const { type } = columnConfigs[colIdx];
                  const isId = type === "id";
                  const cellKey = `${rowIdx}-${colIdx}`;
                  const str =
                    cell == null
                      ? ""
                      : typeof cell === "number" && isFinite(cell) && !Number.isInteger(cell)
                      ? parseFloat(cell.toFixed(2)).toString()
                      : String(cell);
                  return (
                    <Td
                      key={colIdx}
                      title={str}
                      $copyable={isId}
                      onClick={isId ? () => handleCopy(cellKey, str) : undefined}
                    >
                      {str}
                      {isId && copiedKey === cellKey && (
                        <CopiedBadge>&#10003; Copied</CopiedBadge>
                      )}
                    </Td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </ScrollArea>
      <PaginationBar data-testid="datatable-footer">
        <PageSizeLabel>Rows per page:</PageSizeLabel>
        <PageSizeSelect
          data-testid="datatable-pagesize-select"
          value={pageSize}
          onChange={handlePageSizeChange}
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </PageSizeSelect>
        <RangeText data-testid="datatable-range">
          {startIdx + 1}&ndash;{endIdx} of {totalRows}
        </RangeText>
        <NavButton
          data-testid="datatable-prev-button"
          onClick={() => setCurrentPage((p) => p - 1)}
          disabled={currentPage === 1}
        >
          Prev
        </NavButton>
        <NavButton
          data-testid="datatable-next-button"
          onClick={() => setCurrentPage((p) => p + 1)}
          disabled={currentPage >= totalPages}
        >
          Next
        </NavButton>
        <DownloadButton data-testid="datatable-download-button" onClick={handleDownload}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Download CSV
        </DownloadButton>
      </PaginationBar>
    </Wrapper>
  );
}
